import { Ban, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import UnifiedSearchBar from "../pages/provider/shared/components/UnifiedSearchBar";
import contractsService, { ContractDetails } from "../services/contractsService";
import profileExclusionsService, { ContractExclusion } from "../services/profileExclusionsService";
import { SearchResult } from "../services/stockSearchService";
import ConfirmationModal from "./ConfirmationModal";

/** Exclusion enriched with contract details for display */
interface EnrichedExclusion extends ContractExclusion {
  symbol?: string;
  longName?: string;
}

interface ContractExclusionsCardProps {
  onExclusionsChanged?: () => void;
  appearance?: "default" | "alpha";
}

/**
 * Self-contained component for managing user's contract exclusions.
 * Uses profileExclusionsService for authenticated user's exclusions.
 */
const ContractExclusionsCard: React.FC<ContractExclusionsCardProps> = ({
  onExclusionsChanged,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const [exclusions, setExclusions] = useState<EnrichedExclusion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingExclusion, setAddingExclusion] = useState(false);
  const [filterText, setFilterText] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [exclusionToDelete, setExclusionToDelete] = useState<EnrichedExclusion | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchExclusions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await profileExclusionsService.getExclusions();
      const rawExclusions = response.exclusions || [];

      if (rawExclusions.length === 0) {
        setExclusions([]);
        return;
      }

      const conIds = rawExclusions.map(e => e.conId);
      let contractMap: Record<number, ContractDetails> = {};
      try {
        contractMap = await contractsService.getContractDetailsMap(conIds);
      } catch (err) {
        console.warn("Failed to resolve contract details, displaying conIds only:", err);
      }

      const enriched: EnrichedExclusion[] = rawExclusions.map(exc => ({
        ...exc,
        symbol: contractMap[exc.conId]?.symbol,
        longName: contractMap[exc.conId]?.longName,
      }));

      setExclusions(enriched);
    } catch (err: any) {
      const status = err.response?.status || err.status;
      if (status === 404) {
        setExclusions([]);
      } else {
        console.error("Failed to fetch exclusions:", err);
        setError(err.response?.data?.error || err.message || "Failed to load exclusions");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExclusions();
  }, [fetchExclusions]);

  const existingConIds = useMemo(
    () => new Set(exclusions.map(e => e.conId)),
    [exclusions]
  );

  const handleAddExclusion = async (stock: SearchResult) => {
    if (!stock.conId) {
      setError("Selected stock does not have a valid contract ID");
      return;
    }

    if (existingConIds.has(stock.conId)) {
      setError(`${stock.ticker} is already in your exclusions list`);
      return;
    }

    setAddingExclusion(true);
    setError(null);
    try {
      await profileExclusionsService.createExclusion(stock.conId);
      await fetchExclusions();
      onExclusionsChanged?.();
    } catch (err: any) {
      const status = err.response?.status || err.status;
      if (status === 409) {
        setError(`${stock.ticker} is already excluded`);
      } else {
        setError(err.response?.data?.error || err.message || "Failed to add exclusion");
      }
    } finally {
      setAddingExclusion(false);
    }
  };

  const openDeleteModal = (exclusion: EnrichedExclusion) => {
    setExclusionToDelete(exclusion);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!exclusionToDelete) return;

    setDeleteLoading(true);
    try {
      await profileExclusionsService.deleteExclusion(exclusionToDelete.conId);
      setDeleteModalOpen(false);
      setExclusionToDelete(null);
      await fetchExclusions();
      onExclusionsChanged?.();
    } catch (err: any) {
      console.error("Failed to delete exclusion:", err);
      setError(err.response?.data?.error || err.message || "Failed to remove exclusion");
      setDeleteModalOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredExclusions = useMemo(() => {
    if (!filterText.trim()) return exclusions;
    const q = filterText.toLowerCase();
    return exclusions.filter(
      e =>
        (e.symbol && e.symbol.toLowerCase().includes(q)) ||
        (e.longName && e.longName.toLowerCase().includes(q)) ||
        String(e.conId).includes(q)
    );
  }, [exclusions, filterText]);

  const thClass = isAlpha
    ? "px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-v7-ink/55"
    : "px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400";
  const emptyTextClass = isAlpha
    ? "text-sm italic py-4 text-v7-ink/50"
    : "text-sm italic py-4 text-gray-400 dark:text-gray-500";

  return (
    <>
      <div className={isAlpha
        ? "bg-v7-white rounded-v7-panel border border-v7-ink/15"
        : "bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator"}>
        <div className={`p-6 border-b ${isAlpha ? "border-v7-ink/10" : "border-gray-200 dark:border-rp-separator"}`}>
          <h2 className={`text-base font-bold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"} flex items-center gap-2`}>
            <Ban className="w-5 h-5" />
            Contract Exclusions
          </h2>
          <p className={`text-sm ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-gray-400"} mt-1`}>
            Stocks in this list will be excluded from all trading and rebalancing operations
          </p>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className={isAlpha
              ? "p-3 rounded-v7-panel text-sm bg-v7-rust/5 text-v7-rust border border-v7-rust/30"
              : "p-3 rounded-lg text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"}>
              {error}
            </div>
          )}

          <div className="flex items-center gap-2">
            <UnifiedSearchBar
              existingConIds={existingConIds}
              entityLabel="Exclusions"
              filterText={filterText}
              onFilterTextChange={setFilterText}
              onAddStock={handleAddExclusion}
              appearance={appearance}
            />
            {addingExclusion && (
              <span className={`text-xs ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"} shrink-0`}>Adding...</span>
            )}
          </div>

          <div>
            <h3 className={`text-[10px] font-semibold uppercase tracking-widest ${isAlpha ? "text-v7-ink/55" : "text-gray-500 dark:text-gray-400"} mb-3`}>
              Excluded Stocks ({filteredExclusions.length}{filterText ? ` of ${exclusions.length}` : ""})
            </h3>

            {loading && exclusions.length === 0 ? (
              <div className="flex justify-center py-8">
                <div className={`animate-pulse h-6 w-6 rounded-full border-b-2 ${isAlpha ? "border-v7-bronze-dark" : "border-rp-amber"}`} />
              </div>
            ) : exclusions.length === 0 ? (
              <p className={emptyTextClass}>
                No stocks excluded. Use the search above to add stocks to your exclusion list.
              </p>
            ) : filteredExclusions.length === 0 ? (
              <p className={emptyTextClass}>
                No exclusions match "{filterText}"
              </p>
            ) : (
              <div className={`overflow-x-auto ${isAlpha ? "rounded-v7-panel border border-v7-ink/15" : "rounded-lg border border-gray-200 dark:border-rp-separator"}`}>
                <table className={`min-w-full divide-y ${isAlpha ? "divide-v7-ink/10" : "divide-gray-200 dark:divide-rp-separator"}`}>
                  <thead className={isAlpha ? "bg-v7-paper" : "bg-gray-50 dark:bg-rp-surface-raised"}>
                    <tr>
                      <th className={thClass}>
                        Symbol
                      </th>
                      <th className={thClass}>
                        Name
                      </th>
                      <th className={thClass}>
                        Added
                      </th>
                      <th className={thClass}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isAlpha ? "divide-v7-ink/10" : "divide-gray-200 dark:divide-rp-separator"}`}>
                    {filteredExclusions.map(exclusion => (
                      <tr
                        key={exclusion.id}
                        className={`${isAlpha ? "hover:bg-v7-paper" : "hover:bg-gray-50 dark:hover:bg-rp-surface-raised"} transition-colors`}
                      >
                        <td className={`px-4 py-3 whitespace-nowrap text-sm font-mono font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
                          {exclusion.symbol || `ConId: ${exclusion.conId}`}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}`}>
                          {exclusion.longName || "-"}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
                          {new Date(exclusion.createdAtUtc).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => openDeleteModal(exclusion)}
                            className={isAlpha
                              ? "p-1.5 rounded-full transition-colors text-v7-rust hover:bg-v7-rust/10"
                              : "p-1.5 rounded-full transition-colors text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"}
                            title="Remove from exclusions"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setExclusionToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Remove Exclusion"
        message={`Are you sure you want to remove ${
          exclusionToDelete?.symbol || `ConId ${exclusionToDelete?.conId}`
        } from your exclusions list? This stock will no longer be blocked from trading.`}
        confirmText="Remove"
        cancelText="Cancel"
        isDestructive={true}
        loading={deleteLoading}
        appearance={appearance}
      />
    </>
  );
};

export default ContractExclusionsCard;
